import React, { useState, useEffect, useCallback } from 'react';
import { categoriesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, FolderOpen, ArrowLeft, Save, X, Loader2, Languages, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ImageLibrary from './ImageLibrary';
import { SortableList } from './SortableList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoriesPanel = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [batchTranslating, setBatchTranslating] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [formData, setFormData] = useState({
    name: { es: '', en: '', cat: '', fr: '' },
    parent_type: 'bar',
    order: 0,
    is_active: true,
    seccion: '',
    image: ''
  });

  const parentTypes = [
    { id: 'bar', label: 'Bar' },
    { id: 'cafe', label: 'Cafetería' },
    { id: 'degustacion', label: 'Degustación' },
    { id: 'tienda', label: 'Tienda' }
  ];

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesApi.getAll({ active_only: false });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || { es: '', en: '', cat: '', fr: '' },
        parent_type: category.parent_type || 'bar',
        order: category.order || 0,
        is_active: category.is_active !== false,
        seccion: category.seccion || '',
        image: category.image || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: { es: '', en: '', cat: '', fr: '' },
        parent_type: 'bar',
        order: categories.length,
        is_active: true,
        seccion: '',
        image: ''
      });
    }
    setShowDialog(true);
  };

  const handleAutoTranslate = async () => {
    if (!formData.name.es) {
      toast.error('Escribe el nombre en español primero');
      return;
    }
    setTranslating(true);
    try {
      const res = await fetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: formData.name.es, source_lang: 'es', target_langs: ['cat', 'en', 'fr'] })
      });
      const data = await res.json();
      if (data.translations) {
        setFormData(prev => ({ ...prev, name: { ...prev.name, ...data.translations } }));
        toast.success('Traducción completada');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Error al traducir');
    } finally {
      setTranslating(false);
    }
  };

  const handleBatchTranslate = async () => {
    const token = localStorage.getItem('admin_token');
    setBatchTranslating(true);
    toast.info('Traduciendo todo... esto puede tardar unos segundos');
    try {
      const res = await fetch(`${API_URL}/api/translate/batch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const msg = `${data.categories_translated} categorías y ${data.products_translated} productos traducidos`;
        toast.success(msg);
        fetchCategories();
      } else {
        toast.error(data.detail || 'Error al traducir');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setBatchTranslating(false);
    }
  };

  const handleReorder = async (parentType, newItems) => {
    // Optimistic update
    setCategories(prev => {
      const others = prev.filter(c => c.parent_type !== parentType);
      return [...others, ...newItems];
    });
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/categories/reorder/batch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order: newItems.map((item, idx) => ({ id: item.id, sort_order: idx })) })
      });
      toast.success('Orden guardado');
    } catch (e) {
      toast.error('Error al reordenar');
      fetchCategories();
    }
  };

  const handleSave = async () => {
    if (!formData.name.es) {
      toast.error('El nombre en español es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        parent_type: formData.parent_type,
        sort_order: formData.order,
        is_active: formData.is_active,
        seccion: formData.seccion || null,
        image: formData.image
      };
      if (editingCategory) {
        await categoriesApi.update(editingCategory.id, payload);
        toast.success('Categoría actualizada');
      } else {
        await categoriesApi.create(payload);
        toast.success('Categoría creada');
      }
      setShowDialog(false);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!confirm(`¿Eliminar la categoría "${category.name?.es || category.id}"?`)) return;
    
    try {
      await categoriesApi.delete(category.id);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error al eliminar');
    }
  };

  // Group categories by parent_type
  const groupedCategories = categories.reduce((acc, cat) => {
    const type = cat.parent_type || 'otros';
    if (!acc[type]) acc[type] = [];
    acc[type].push(cat);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/admin-menu')} className="rounded-lg">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-medium text-gray-800">Categorías</h1>
          <p className="text-sm text-gray-500">{categories.length} categorías</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-green-600 hover:bg-green-700 rounded-lg">
          <Plus size={18} className="mr-1" />
          <span className="hidden sm:inline">Nueva</span>
        </Button>
        <Button
          variant="outline"
          onClick={handleBatchTranslate}
          disabled={batchTranslating}
          className="rounded-lg gap-1.5"
          data-testid="batch-translate-all-btn"
        >
          {batchTranslating ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
          <span className="hidden sm:inline">Traducir todo</span>
        </Button>
      </div>

      {/* Categories by Type */}
      <div className="space-y-6">
        {parentTypes.map(type => {
          const typeCats = groupedCategories[type.id] || [];
          if (typeCats.length === 0) return null;
          
          return (
            <div key={type.id}>
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 px-1">
                {type.label} ({typeCats.length})
              </h2>
              <div className="space-y-2">
                <SortableList
                  items={typeCats}
                  onReorder={(newItems) => handleReorder(type.id, newItems)}
                  renderItem={(cat) => (
                    <Card className="border border-gray-100">
                      <CardContent className="p-3 md:p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {cat.image ? (
                              <img src={cat.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <FolderOpen size={20} className="text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate">{cat.name?.es || cat.id}</p>
                            <p className="text-xs text-gray-500 truncate">
                              CAT: {cat.name?.cat || '-'} | EN: {cat.name?.en || '-'} | FR: {cat.name?.fr || '-'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOpenDialog(cat)}
                              className="h-9 w-9 p-0"
                              data-testid={`edit-category-${cat.id}`}
                            >
                              <Edit2 size={16} className="text-gray-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete(cat)}
                              className="h-9 w-9 p-0 hover:text-red-600"
                            >
                              <Trash2 size={16} className="text-gray-500" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Names by language with auto-translate */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Nombre por idioma</label>
                <Button type="button" variant="outline" size="sm" onClick={handleAutoTranslate} disabled={translating} className="text-xs h-7" data-testid="category-auto-translate-btn">
                  {translating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Languages size={14} className="mr-1" />}
                  Traducir auto
                </Button>
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Español *</label>
                <Input
                  value={formData.name.es}
                  onChange={(e) => setFormData({
                    ...formData,
                    name: { ...formData.name, es: e.target.value }
                  })}
                  placeholder="Nombre en español"
                  data-testid="category-name-es"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Català</label>
                  <Input
                    value={formData.name.cat}
                    onChange={(e) => setFormData({
                      ...formData,
                      name: { ...formData.name, cat: e.target.value }
                    })}
                    placeholder="Català"
                    className="text-sm"
                    data-testid="category-name-cat"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">English</label>
                  <Input
                    value={formData.name.en}
                    onChange={(e) => setFormData({
                      ...formData,
                      name: { ...formData.name, en: e.target.value }
                    })}
                    placeholder="English"
                    className="text-sm"
                    data-testid="category-name-en"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Français</label>
                  <Input
                    value={formData.name.fr}
                    onChange={(e) => setFormData({
                      ...formData,
                      name: { ...formData.name, fr: e.target.value }
                    })}
                    placeholder="Français"
                    className="text-sm"
                    data-testid="category-name-fr"
                  />
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">Imagen</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  {formData.image ? (
                    <div className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
                      <img src={formData.image} alt="Preview" className="w-12 h-12 object-cover rounded" />
                      <span className="flex-1 text-sm text-gray-600 truncate">{formData.image.split('/').pop()}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({ ...formData, image: '' })} className="h-8 w-8 p-0 text-red-500">
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
                      <ImageIcon size={24} className="mx-auto text-gray-300 mb-1" />
                      <p className="text-xs text-gray-400">Sin imagen</p>
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" onClick={() => setShowImageLibrary(true)} className="flex-shrink-0" data-testid="category-image-library-btn">
                  <ImageIcon size={16} className="mr-1" /> Biblioteca
                </Button>
              </div>
            </div>

            {/* Parent Type */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Experiencia</label>
              <div className="grid grid-cols-2 gap-2">
                {parentTypes.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, parent_type: type.id })}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      formData.parent_type === type.id
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Order */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Orden</label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>

            {/* Sección POS override (Nuevo Pedido) */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Sección en POS "Nuevo Pedido"
                <span className="ml-2 text-xs font-normal text-gray-400">
                  Decide en qué pestaña (Barra / Cocina / Tienda) vive esta categoría.
                </span>
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: '', label: 'Automático', hint: 'Voto mayoritario' },
                  { id: 'barra', label: 'Barra', hint: '' },
                  { id: 'cocina', label: 'Cocina', hint: '' },
                  { id: 'tienda', label: 'Tienda', hint: '' },
                ].map(opt => (
                  <button
                    key={opt.id || 'auto'}
                    type="button"
                    onClick={() => setFormData({ ...formData, seccion: opt.id })}
                    data-testid={`cat-seccion-${opt.id || 'auto'}`}
                    className={`px-3 py-2 rounded-lg text-sm border transition-all ${
                      (formData.seccion || '') === opt.id
                        ? 'bg-emerald-500 border-emerald-500 text-white font-semibold'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-400'
                    }`}
                  >
                    <div>{opt.label}</div>
                    {opt.hint && <div className="text-[10px] opacity-80 font-normal">{opt.hint}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              <X size={16} className="mr-1" />
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Save size={16} className="mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageLibrary
        open={showImageLibrary}
        onClose={() => setShowImageLibrary(false)}
        onSelect={(imageUrl) => {
          setFormData(prev => ({ ...prev, image: imageUrl }));
          setShowImageLibrary(false);
        }}
        currentImage={formData.image}
      />
    </div>
  );
};

export default CategoriesPanel;
